import { readDecodedHash } from "@/utils/url-hash.util";
const OVERVIEW = "overview";
const HIDDEN_CLASS = "hidden";
const ANCHOR_ATTRIBUTE = "data-catalog-anchor";
/**
 * Product catalog: an application selector above a table of systems.
 * The server renders the overview and one panel per application. This component only decides which header and
 * panel are visible: selecting an application shows its panel, selecting it again returns to the overview.
 * Each card carries its anchor in data-catalog-anchor (not as an HTML id, so two catalogs or a page section with
 * the same name never produce duplicate ids). A URL hash matching it selects that application on load and on
 * hashchange, and selecting a card writes its anchor to the URL so the selection can be shared.
 */
export const ProductCatalog = (element) => {
    const cards = Array.from(element.querySelectorAll("[data-catalog-application]"));
    if (cards.length === 0) {
        return undefined;
    }
    const headers = Array.from(element.querySelectorAll("[data-catalog-header]"));
    const panels = Array.from(element.querySelectorAll("[data-catalog-panel]"));
    let selectedId = OVERVIEW;
    // Whether the current hash names one of this catalog's applications, so a later hash change away from it
    // returns the catalog to the overview while hashes owned by other blocks leave it alone.
    let hashIsOwn = false;
    const show = (id) => {
        selectedId = id;
        cards.forEach((card) => {
            card.setAttribute("aria-selected", card.getAttribute("data-catalog-application") === id ? "true" : "false");
        });
        headers.forEach((header) => {
            header.classList.toggle(HIDDEN_CLASS, header.getAttribute("data-catalog-header") !== id);
        });
        panels.forEach((panel) => {
            panel.classList.toggle(HIDDEN_CLASS, panel.getAttribute("data-catalog-panel") !== id);
        });
    };
    const findCardByAnchor = (anchor) => anchor ? cards.find((card) => card.getAttribute(ANCHOR_ATTRIBUTE) === anchor) : undefined;
    // replaceState keeps the page from jumping and does not fire hashchange.
    const writeHash = (card) => {
        const { pathname, search } = window.location;
        const anchor = card?.getAttribute(ANCHOR_ATTRIBUTE);
        if (anchor) {
            window.history.replaceState(window.history.state, "", `${pathname}${search}#${encodeURIComponent(anchor)}`);
            hashIsOwn = true;
        }
        else if (findCardByAnchor(readDecodedHash())) {
            // Only clear a hash this catalog owns; another anchor on the page stays untouched.
            window.history.replaceState(window.history.state, "", `${pathname}${search}`);
            hashIsOwn = false;
        }
    };
    // No element carries the anchor as its id, so the browser does not scroll to it; scroll the first catalog on
    // the page that has this anchor, so two catalogs sharing an application do not fight over the scroll position.
    const scrollIntoViewIfFirst = (anchor) => {
        const first = document.querySelector(`[${ANCHOR_ATTRIBUTE}="${CSS.escape(anchor)}"]`);
        if (first && element.contains(first)) {
            element.scrollIntoView({ block: "start" });
        }
    };
    const selectFromHash = () => {
        const anchor = readDecodedHash();
        const id = findCardByAnchor(anchor)?.getAttribute("data-catalog-application");
        if (id) {
            show(id);
            hashIsOwn = true;
            scrollIntoViewIfFirst(anchor);
            return;
        }
        // The hash no longer names an application of this catalog: keep the URL and the UI in agreement.
        if (selectedId !== OVERVIEW && (hashIsOwn || !anchor)) {
            show(OVERVIEW);
        }
        hashIsOwn = false;
    };
    const onClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const card = target.closest("[data-catalog-application]");
        if (!card || !element.contains(card)) {
            return;
        }
        const id = card.getAttribute("data-catalog-application");
        if (!id) {
            return;
        }
        const next = id === selectedId ? OVERVIEW : id;
        show(next);
        writeHash(next === OVERVIEW ? undefined : card);
    };
    element.addEventListener("click", onClick);
    window.addEventListener("hashchange", selectFromHash);
    show(OVERVIEW);
    selectFromHash();
    return () => {
        element.removeEventListener("click", onClick);
        window.removeEventListener("hashchange", selectFromHash);
    };
};
